import React, { useState } from "react";
import styled from "styled-components";
import { InitialStateType } from "../types";
import { TextField, useTheme } from "cayo.ui";
import { wizardMessages } from "../messages";
import { useIntl, FormattedMessage } from "react-intl";
import SpecifyCredentials from "./SpecifyCredentials";
import { validateCred } from "../api";
import { sortRecords } from "../service";
import { commonMessages } from "@cayo/shared/messages/common";
import { TooltipHost } from "@fluentui/react/lib/Tooltip";
import {
  MessageBar,
  MessageBarType,
  PrimaryButton,
  Icon,
  Checkbox,
  DefaultButton,
  Dialog,
  DialogFooter,
  mergeStyles,
} from "@fluentui/react";

const rootStyle = mergeStyles({
  "::after": {
    inset: "-20px",
  },
});

type CreateConnectionAccountsProps = {
  state: InitialStateType;
  credentialsFilled: boolean;
  isHosted: boolean;
  dispatch: (data: any) => void;
  setSignedIn: (props: boolean) => void;
  handleLoading: (text: string | undefined) => void;
};

const stateIcon: { [key: string]: string } = {
  success: "Completed",
  error: "ErrorBadge",
};

const CreateConnectionAccounts = ({
  state,
  isHosted,
  credentialsFilled,
  setSignedIn,
  dispatch,
  handleLoading,
}: CreateConnectionAccountsProps) => {
  const [hidden, setHidden] = useState(true);
  const intl = useIntl();
  const { cayoTheme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [signInState, setSignInState] = useState("");

  const managedAccountName = state?.creationForm?.managedAccountName;
  const checkedAdmin = state?.creationForm?.credentialUsage === "managedServiceAccountAdmin";

  const validate = () => {
    setLoading(true);
    handleLoading(intl.formatMessage(commonMessages.loading));
    validateCred(state.credentials)
      .then((res) => {
        const partitionData = res?.partitions || [];
        dispatch({
          type: "add-partitions",
          payload: {
            partitions: sortRecords(partitionData.filter((item) => item.hasAdminRights)),
            partitionsNoPermission: partitionData.filter((item) => !item.hasAdminRights),
          },
        });
        setSignInState("success");
        setError("");
        setSignedIn(true);
      })
      .catch((e) => {
        setError(e?.message || "invalid credentials");
        setSignInState("error");
        setSignedIn(false);
      })
      .finally(() => {
        setLoading(false);
        handleLoading(undefined);
      });
  };

  const handleChangeAccountName = (value: string) => {
    dispatch({
      type: "update",
      payload: {
        form: "creationForm",
        data: {
          managedAccountName: value,
        },
      },
    });
  };

  const handleChangeConfig = (value: string) => {
    dispatch({
      type: "update",
      payload: {
        form: "creationForm",
        data: {
          credentialUsage: value,
        },
      },
    });
  };

  return (
    <Wrapper>
      {error && (
        <MessageBar messageBarType={MessageBarType.error} truncated={true}>
          {error}
        </MessageBar>
      )}
      <MessageBar
        messageBarType={MessageBarType.info}
        truncated={true}
        styles={{
          root: {
            marginBottom: 15,
            background: cayoTheme.wizard.colors.active,
          },
        }}
      >
        {intl.formatMessage(
          isHosted
            ? wizardMessages.createConnectionAccountsMsgHosted
            : wizardMessages.createConnectionAccountsMsg,
          {
            link: (
              <a
                href={"https://support.cayosoft.com/hc/en-us/articles/39979140146701"}
                target="_blank"
                rel="noreferrer"
              >
                <FormattedMessage
                  id={wizardMessages.LearnMore.id}
                  defaultMessage={wizardMessages.LearnMore.defaultMessage}
                />
              </a>
            ),
          }
        )}
      </MessageBar>
      <ButtonWrap>
        <PrimaryButton disabled={loading} onClick={() => setHidden(false)}>
          Sign In
        </PrimaryButton>
        {signInState && (
          <Icon
            iconName={stateIcon[signInState]}
            styles={{
              root: {
                color:
                  signInState === "success"
                    ? cayoTheme.brandColors.success
                    : cayoTheme.brandColors.error,
              },
            }}
            aria-describedby="state"
          />
        )}
      </ButtonWrap>
      <TextField
        tooltip={intl.formatMessage(wizardMessages.gmsaFieldAccountNameTooltip)}
        label={intl.formatMessage(wizardMessages.gmsaFieldAccountName)}
        required
        value={managedAccountName}
        setValue={(value) => handleChangeAccountName(value)}
        styles={{
          root: {
            selectors: {
              ".ms-Button ": {
                width: "0px",
              },
            },
          },
        }}
      />
      {!isHosted && (
        <Record>
          <Tooltip>
            <Checkbox
              required
              checked={checkedAdmin}
              id="Enable gmsa Admin"
              label={intl.formatMessage(wizardMessages.gmsaAdminCheckbox)}
              onChange={(_, checked) => {
                handleChangeConfig(
                  checked ? "managedServiceAccountAdmin" : "managedServiceAccount"
                );
              }}
            />
            <IconWithTooltip
              tooltipText={intl.formatMessage(wizardMessages.addAccountToPrivileged, {
                link: (
                  <a
                    href={"https://support.cayosoft.com/hc/en-us/articles/360041734032"}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <FormattedMessage
                      id={wizardMessages.LearnMore.id}
                      defaultMessage={wizardMessages.LearnMore.defaultMessage}
                    />
                  </a>
                ),
              })}
            />
          </Tooltip>
        </Record>
      )}
      <Dialog
        hidden={hidden}
        minWidth={524}
        onDismiss={() => setHidden(true)}
        dialogContentProps={{
          showCloseButton: true,
          title: "Sign In",
          styles: {
            innerContent: {
              alignItems: "center",
              display: "flex",
              minHeight: "50px",
            },
            subText: { fontSize: 16, fontWeight: 400 },
            title: { fontWeight: 600 },
          },
        }}
        modalProps={{
          isBlocking: true,
          topOffsetFixed: true,
          styles: {
            main: { maxWidth: 450 },
            scrollableContent: { height: "100%" },
          },
        }}
      >
        <Wrapper>
          <SpecifyCredentials
            state={state}
            message={intl.formatMessage(wizardMessages.createConnectionAccountsDialog)}
            dispatch={dispatch}
          />
        </Wrapper>
        <DialogFooter>
          <DefaultButton
            onClick={() => setHidden(true)}
            text={intl.formatMessage(commonMessages.cancel)}
          />
          <PrimaryButton
            onClick={() => {
              setHidden(true);
              validate();
            }}
            text={intl.formatMessage(wizardMessages.signIn)}
            disabled={!credentialsFilled}
          />
        </DialogFooter>
      </Dialog>
    </Wrapper>
  );
};

const IconWithTooltip = ({ tooltipText }: { tooltipText: any }) => (
  <>
    <TooltipHost
      content={tooltipText}
      calloutProps={{
        styles: {
          root: rootStyle,
        },
      }}
      id={"adtooltip"}
    >
      <Icon iconName={"Info"} aria-describedby={"adicon"} />
    </TooltipHost>
  </>
);

const ButtonWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;

  button {
    width: 100px;
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Record = styled.div`
  display: flex;
  align-items: baseline;
  gap: 4px;
  font-weight: 500;
  margin-top: 20px;
`;

const Tooltip = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

export default CreateConnectionAccounts;
